import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MoodPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [moods, setMoods] = useState([]);
  const [selectedMood, setSelectedMood] = useState('');
  const [energyLevel, setEnergyLevel] = useState(3);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const moodOptions = [
    { value: 'great', emoji: '😊', label: 'Great', color: 'green' },
    { value: 'good', emoji: '🙂', label: 'Good', color: 'blue' },
    { value: 'okay', emoji: '😐', label: 'Okay', color: 'yellow' },
    { value: 'stressed', emoji: '😰', label: 'Stressed', color: 'orange' },
    { value: 'anxious', emoji: '😟', label: 'Anxious', color: 'purple' },
    { value: 'sad', emoji: '😔', label: 'Sad', color: 'gray' }
  ];

  useEffect(() => {
    if (user) {
      fetchMoods();
    }
  }, [user]);

  const fetchMoods = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/mood`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setMoods(response.data);
    } catch (error) {
      console.error('Failed to fetch moods:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMood) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/mood`,
        {
          mood: selectedMood,
          energy_level: energyLevel,
          notes: notes || null
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // Reset form
      setSelectedMood('');
      setEnergyLevel(3);
      setNotes('');
      
      // Refresh moods
      await fetchMoods();
    } catch (error) {
      console.error('Failed to save mood:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-mentality-50 via-teal-50 to-cyan-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mentality-300"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-mentality-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-mentality-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center space-x-3">
              <img 
                src="/mentality-logo.jpg" 
                alt="Mentality Logo" 
                className="w-10 h-10 md:w-12 md:h-12 object-contain"
              />
              <div className="hidden sm:block">
                <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-mentality-300 to-mentality-600 bg-clip-text text-transparent">
                  Mentality
                </h1>
                <p className="text-xs text-gray-500">The College Mentality</p>
              </div>
            </Link>
            <div className="flex items-center space-x-2 md:space-x-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm">Dashboard</Button>
              </Link>
              <Link to="/sessions">
                <Button variant="ghost" size="sm">Sessions</Button>
              </Link>
              <Link to="/mood">
                <Button variant="ghost" size="sm" className="bg-mentality-100 text-mentality-700">Mood</Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={() => logout().then(() => navigate('/'))}
                className="border-mentality-200 hover:bg-mentality-50"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Mood Tracker 💭
          </h2>
          <p className="text-gray-600">
            Check in with yourself. Track how you're feeling over time.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Log Mood Form */}
          <div className="lg:col-span-1">
            <Card className="border-2 border-mentality-200 shadow-xl sticky top-24">
              <CardHeader>
                <CardTitle>How are you feeling?</CardTitle>
                <CardDescription>
                  Take a moment to check in with yourself
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Mood Selection */}
                  <div>
                    <Label className="mb-3 block">Select your mood</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {moodOptions.map((mood) => (
                        <button
                          key={mood.value}
                          type="button"
                          onClick={() => setSelectedMood(mood.value)}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            selectedMood === mood.value
                              ? 'border-mentality-400 bg-mentality-50 shadow-lg scale-105'
                              : 'border-gray-200 hover:border-mentality-300'
                          }`}
                        >
                          <div className="text-3xl mb-1">{mood.emoji}</div>
                          <div className="text-sm font-medium">{mood.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Energy Level */}
                  <div>
                    <Label className="mb-3 block">
                      Energy Level: {energyLevel}/5
                    </Label>
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={energyLevel}
                      onChange={(e) => setEnergyLevel(parseInt(e.target.value))}
                      className="w-full h-2 bg-mentality-100 rounded-lg appearance-none cursor-pointer accent-mentality-400"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Low</span>
                      <span>High</span>
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <Label htmlFor="mood-notes" className="mb-2 block">
                      Notes (Optional)
                    </Label>
                    <Textarea
                      id="mood-notes"
                      placeholder="What's on your mind?"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={4}
                      className="border-mentality-200 focus:border-mentality-400"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={!selectedMood || saving}
                    className="w-full bg-gradient-to-r from-mentality-300 to-mentality-500 hover:from-mentality-400 hover:to-mentality-600 text-white"
                    size="lg"
                  >
                    {saving ? 'Saving...' : 'Log Mood'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Mood History */}
          <div className="lg:col-span-2">
            <Card className="border-2 border-mentality-200 shadow-xl">
              <CardHeader>
                <CardTitle>Your Mood History</CardTitle>
                <CardDescription>
                  {moods.length > 0
                    ? `You've logged ${moods.length} mood entries`
                    : 'Start logging to see your mood patterns'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {moods.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📝</div>
                    <p className="text-gray-500 mb-2">No mood entries yet</p>
                    <p className="text-sm text-gray-400">
                      Start tracking to understand your emotional patterns
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {moods.map((entry, index) => {
                      const moodConfig = moodOptions.find(m => m.value === entry.mood);
                      return (
                        <div key={entry.id}>
                          <div className="flex items-start space-x-4 p-4 rounded-lg hover:bg-mentality-50 transition-colors">
                            <div className="text-4xl">{moodConfig?.emoji}</div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <span className="font-semibold text-lg">
                                    {moodConfig?.label}
                                  </span>
                                  <span className="text-sm text-gray-500 ml-3">
                                    Energy: {entry.energy_level}/5
                                  </span>
                                </div>
                                <span className="text-sm text-gray-400">
                                  {new Date(entry.created_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>
                              {entry.notes && (
                                <p className="text-sm text-gray-600 mt-2">
                                  {entry.notes}
                                </p>
                              )}
                            </div>
                          </div>
                          {index < moods.length - 1 && <Separator className="my-2" />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MoodPage;
